import { ReactNode, useState } from 'react';
import styled from "@emotion/styled";
import { ButtonProps } from '@mui/material';
import Dialog from '@/components/_common/Dialog';

interface UseConfirmationPromptParams {
  message: string;
  title: string;
  titleIcon?: ReactNode;
  buttons?: {
    submitText: string;
    cancelText: string;
  };
  buttonColor?: ButtonProps['color'];
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
}

const useConfirmationPrompt = ({ message, title, titleIcon, buttons, buttonColor, onConfirm, onCancel }: UseConfirmationPromptParams) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const { submitText, cancelText } = buttons ?? {
    submitText: 'Yes, abandon changes',
    cancelText: 'No, stay here'
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await Promise.resolve(onConfirm());
    } finally {
      setLoading(false);
      setDialogVisible(false);
    }
  };

  const closeLoadingDialog = () => {
    setLoading(false);
    setDialogVisible(false);
  };

  const dialog = (
    <StyledDialog
      open={dialogVisible}
      title={title}
      titleIcon={titleIcon}
      buttonColor={buttonColor}
      submitText={submitText}
      onSubmit={handleConfirm}
      cancelText={cancelText}
      maxWidth={480}
      onCancel={() => {
        setDialogVisible(false);
        onCancel?.();
      }}
      loading={loading}
    >
      {message}
    </StyledDialog>
  );

  return { dialog, setDialogVisible, dialogVisible, closeLoadingDialog };
};

export default useConfirmationPrompt;

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiPaper-root': {
    maxWidth: 450
  }
}));
